import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef
} from 'react'
import { Editor as EditorDraft } from 'react-draft-wysiwyg'

import { EditorState, ContentState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { AutenticacaoContainer, Visao } from '../autenticacao'
import { ModalRef } from '../modal/tipos'
import { Cores } from '../styles/cores'
import { ModalLimiteCaracter } from './modal-limite-caracter/modal-limite-caracter'
import ptBR from './ptBR'
import { Container } from './styles'
import { EditorRef, EditorProps } from './tipos'

export const Editor = forwardRef<EditorRef, EditorProps>(
  (
    {
      id,
      defaultValue,
      urlUploadImage,
      minHeight,
      erro,
      readOnly,
      maxHeight,
      placeholder,
      onValorChange
    },
    ref
  ) => {
    const contentState = useMemo(() => {
      const { contentBlocks, entityMap } = htmlToDraft(defaultValue || '')
      return ContentState.createFromBlockArray(contentBlocks, entityMap)
    }, [defaultValue])

    const modalLimiteCaracterRef = useRef<ModalRef>(null)
    const { visao } = AutenticacaoContainer.useContainer()
    const [valor, definirValor] = useState(
      defaultValue
        ? EditorState.createWithContent(contentState)
        : EditorState.createEmpty()
    )

    useEffect(() => {
      const novoEstado = EditorState.createWithContent(contentState)
      definirValor(novoEstado)
    }, [contentState])

    useImperativeHandle(ref, () => ({
      valorHTML: () => {
        return draftToHtml(convertToRaw(valor.getCurrentContent()))
      },
      definirValor: (value: string) => {
        const { contentBlocks, entityMap } = htmlToDraft(value || '')
        const contentState = ContentState.createFromBlockArray(
          contentBlocks,
          entityMap
        )
        const editorState = EditorState.createWithContent(contentState)
        definirValor(editorState)
      },
      limpar: () => {
        definirValor(EditorState.createEmpty())
      }
    }))

    const uploadCallback = () =>
      new Promise(resolve =>
        resolve({
          data: {
            link: urlUploadImage
          }
        })
      )

    const handleEditorStateChange = (content: EditorState) => {
      const plainText = content.getCurrentContent().getPlainText()

      if (visao === Visao.Aluno && plainText.length > 500) {
        const textoLimitado = plainText.substring(0, 500)
        const contentState = ContentState.createFromText(textoLimitado)
        const estadoLimitado = EditorState.createWithContent(contentState)

        definirValor(estadoLimitado)

        onValorChange?.(draftToHtml(convertToRaw(contentState)), textoLimitado)

        modalLimiteCaracterRef?.current?.abrir()

        return
      }

      onValorChange?.(
        draftToHtml(convertToRaw(content.getCurrentContent())),
        plainText
      )
      definirValor(content)
    }

    return (
      <Container id={id}>
        <EditorDraft
          editorState={valor}
          onEditorStateChange={handleEditorStateChange}
          editorStyle={{
            width: '100%',
            minHeight: minHeight || '100px',
            maxHeight: maxHeight || '100px',
            border: readOnly
              ? undefined
              : `1px solid ${erro ? Cores.PERIGO_ERRO : Cores.CINZA_3_ESCURO}`,
            borderRadius: '0 0 4px 4px',
            background: 'white'
          }}
          toolbar={{
            options: [
              'blockType',
              'fontFamily',
              'inline',
              'list',
              'textAlign',
              'colorPicker',
              'link',
              'image',
              'remove',
              'history'
            ],
            inline: {
              options: ['bold', 'italic', 'underline', 'strikethrough']
            },
            image: {
              uploadEnabled: true,
              uploadCallback: urlUploadImage ? uploadCallback : undefined,
              defaultSize: {
                height: 158,
                width: 610
              }
            }
          }}
          localization={{ translations: ptBR }}
          toolbarHidden={readOnly}
          readOnly={readOnly}
          placeholder={placeholder}
        />
        <ModalLimiteCaracter
          ref={modalLimiteCaracterRef}
          backdrop
          id="modal-conclusao"
        />
      </Container>
    )
  }
)
